import { Component, OnInit, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material";
import { CoreLogService } from "../../services/log.service";
import { BaseUnit, MenuSettingsDataModel2, NutritionViewModel, ProductDetailsDataModel3, ProductExtraItemViewModel } from "../../shared/models";
import { CoreLanguageService } from "../../services/language.service";
import Utils from "../../shared/utils";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { ProductDetailsHelper } from "../../services/product_details_helper.service";
import { WebShopService } from "../../services/webShop.service";
import { TranslationContentType, YourAPPProductType } from "../../shared/contentTypes";
import { CoreLanguageManager } from "../../services/ILanguageManager";
import { environment } from "../../../../environments/environment";

export enum HeaderLabel {
  Description = 1,
  Overview,
  CO2,
  Additives,
  Allergens,
  Content,
  Nutritions,
}

@Component({
  selector: "extraAdditionalInfoDialog",
  templateUrl: "./extraAdditionalInfoDialog.component.html",
  styleUrls: ["./extraAdditionalInfoDialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtraAdditionalInfoDialogComponent implements OnInit {
  public headerLabel = HeaderLabel;
  public additionalInfoHeaderList: { label: string, id: HeaderLabel, expanded: boolean }[];

  constructor(public bottomSheetRef: MatBottomSheetRef<ExtraAdditionalInfoDialogComponent>
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _webShopService: WebShopService
    , @Inject(MAT_BOTTOM_SHEET_DATA) private productExtra: ProductExtraItemViewModel) {
  }

  ngOnInit() {
    const me = this;
    me.loadHeaderLabels()
    me.PushChanges();
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("ExtraLabelingDialogComponent", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public get productDetails(): ProductDetailsDataModel3 {
    return this.productExtra.productDetails;
  }

  public get menuSettings(): MenuSettingsDataModel2 {
    return this.productExtra.menuSetting;
  }

  public get hasBaseUnit(): boolean {
    const me = this;
    return me.productExtra.productDetails.item_base_unit_id !== BaseUnit.NONE;
  }

  public get hasAppDescription(): boolean {
    return !Utils.IsNullOrEmpty(this.productExtra.productDetails.details_de || this.productExtra.productDetails.details_en);
  }

  public get description(): string {
    const _description = InternationalisationService.locale === "de" ||
      Utils.IsNullOrWhiteSpace(this.productExtra.productDetails.details_en) ? this.productExtra.productDetails.details_de : this.productExtra.productDetails.details_en;
    if (YourAPPProductType.sot === environment.producttype) {
      return CoreLanguageManager.translate(TranslationContentType.Article_Details, this.productExtra.productDetails.id, _description)
    }
    return _description;
  }

  public get baseUnit(): string {
    const me = this;
    switch (me.productExtra.productDetails.item_base_unit_id) {
      case BaseUnit.PIECE:
        return me.pricePerUnit + " / 1" + (InternationalisationService.locale === "de" ? "Stk" : "pc");
      case BaseUnit.LITER:
        return me.pricePerUnit + " / 1l";
      case BaseUnit.KILO:
        return me.pricePerUnit + " / 1kg";
      case BaseUnit.NONE:
        return "";
      default:
        me._coreLogService.error("ExtraLabelingDialogComponent.baseUnit", "unknown item_base_unit", me.productExtra.productDetails.item_base_unit_id);
        return "";
    }
  }

  /** Product price per unit */
  public get pricePerUnit(): string {
    const me = this;
    const pricePerUnit = Utils.pricePerUnit(me.productExtra.productDetails.baseprice, me.productExtra.productDetails.item_base_factor);
    return me._webShopService.formatCurrency(pricePerUnit);
  }

  /** Caffeine content */
  public get CaffeinelevelText(): string {
    const me = this;
    return me._coreLanguageService.value("phProductDetail_Caffeine_Content")
      .replace("{value}", me.productExtra.productDetails.item_caffeine.toString()) + " mg";
  }

  /**provider name */
  public get providerName() {
    const me = this;
    const _providerName = InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(me.productExtra.productDetails.item_provider_name_en) ? me.productExtra.productDetails.item_provider_name : me.productExtra.productDetails.item_provider_name_en;
    if (YourAPPProductType.sot === environment.producttype) {
      return CoreLanguageManager.translate(TranslationContentType.Article_ItemProviderName, me.productExtra.productDetails.id, _providerName);
    }
    return _providerName
  }

  /**has item provider name */
  public get hasItemProviderName(): boolean {
    const me = this;
    return !Utils.IsNullOrEmpty(me.providerName);
  }

  /**provider adresse */
  public get providerAddr() {
    const me = this;
    const _providerAdrr = InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(me.productExtra.productDetails.item_provider_addr_en) ? me.productExtra.productDetails.item_provider_addr : me.productExtra.productDetails.item_provider_addr_en;
    if (YourAPPProductType.sot === environment.producttype) {
      return CoreLanguageManager.translate(TranslationContentType.Article_ItemProviderAddr, me.productExtra.productDetails.id, _providerAdrr);
    }
    return _providerAdrr;
  }

  /**has item provider Addr. */
  public get hasItemProviderAddr(): boolean {
    const me = this;
    return !Utils.IsNullOrEmpty(me.providerAddr);
  }


  /** Alcohol content */
  public get AlcohollevelText(): string {
    const me = this;
    return me._coreLanguageService.value("phProductDetail_Alcohol_Content")
      .replace("{value}", me.productExtra.productDetails.item_alcohol.toString()) + " % Vol.";
  }

  /** additives */
  public get additives() {
    const me = this;
    const additives = InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(me.productExtra.productDetails.additives_en) ? me.productExtra.productDetails.additives_de : me.productExtra.productDetails.additives_en;
    const additive_keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(additives);
    return me.setContentHtml(me.getLanguageTextFromLabels(additive_keys, InternationalisationService.locale))
  }


  /** alllergy */
  public get allergens() {
    const me = this;
    const allergens = InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(this.productExtra.productDetails.allergens_en) ? this.productExtra.productDetails.allergens_de : this.productExtra.productDetails.allergens_en;
    const allergen_keys = ProductDetailsHelper.extractAdditivesAndAllergiesKeysFromText(allergens);
    return me.setContentHtml(me.getLanguageTextFromLabels(allergen_keys, InternationalisationService.locale))
  }

  /** nutrition */
  public get nutritions(): NutritionViewModel {
    return ProductDetailsHelper.getNutritionsViewModel(this.productExtra.productDetails, InternationalisationService.locale !== "en", InternationalisationService.locale === "en")
  }

  /** item content */
  public get content() {
    const me = this;
    const _itemcontent = InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(me.productExtra.productDetails.item_content_en) ? me.productExtra.productDetails.item_content : me.productExtra.productDetails.item_content_en;
    if (YourAPPProductType.sot === environment.producttype) {
      return CoreLanguageManager.translate(TranslationContentType.Article_ItemContent, me.productExtra.productDetails.id, _itemcontent);
    }
    return _itemcontent
  }

  public loadHeaderLabels() {
    const me = this;
    me.additionalInfoHeaderList = [];
    if (me.menuSettings.use_app_description && me.hasAppDescription) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Description"), id: HeaderLabel.Description, expanded: false })
    }
    if (me.menuSettings.use_eCommerce) {
      if (me.productDetails.item_base_unit_id != BaseUnit.NONE || me.productDetails.item_caffeine > 0 || me.productDetails.item_alcohol > 0 || me.hasItemProviderName) {
        me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Overview"), id: HeaderLabel.Overview, expanded: false })
      }
    }
    if (me.menuSettings.use_co2) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_CO2"), id: HeaderLabel.CO2, expanded: false })
    }
    if (me.menuSettings.use_additives && me.additives.length > 0) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Additives"), id: HeaderLabel.Additives, expanded: false })
    }
    if (me.menuSettings.use_allergens && me.allergens.length > 0) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Allergy"), id: HeaderLabel.Allergens, expanded: false })
    }
    if (me.menuSettings.use_eCommerce && me.content.length > 0) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Content"), id: HeaderLabel.Content, expanded: false })
    }
    if (me.menuSettings.use_nutrition_values && me.nutritions && me.nutritions.nutritionItemViewModels.length > 0) {
      me.additionalInfoHeaderList.push({ label: me._coreLanguageService.value("phProductDetail_Nutrition"), id: HeaderLabel.Nutritions, expanded: false })
    }
  }


  /** Builds a string concattenating descriptions for keys
   * Generated descriptions for keys are seperated by newlines
   * @param keys Array containing keys for allergies or additives
   * @param lng "de", "en"
  */
  private getLanguageTextFromLabels(labels: string[], lng: string): string {
    const translations = ProductDetailsHelper.getTranslationsFromAdditivesAndAllergiesKeys(labels, lng);
    return translations.join("\n");;
  }

  private setContentHtml(content: string): string {
    if (content) {
      return `<p>${content.replace(/(\r\n|\r|\n)/g, "</p><p>")}</p>`;
    } else {
      return "";
    }
  }

  public dismissDialog() {
    const me = this;
    me.bottomSheetRef.dismiss(true);
  }

}
